import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { useCallback, useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../../../baseComponents/Layout/SingleFormLayout'
import LoaderDecider from '../../../../../../../../../baseComponents/LoaderDecider'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { parseCustomerGroups } from '../../../../../../../../../specificComponents/Customers/CustomerGroups'
import { getDictionaryStates } from '../../../../../../../../../utils/globals/countries'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../../../../utils/globals/dictionary'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { CustomerGroupPayload } from '../../../../../../../../../utils/reducers/customers/customerGroups'
import { getDictionaryCountries } from '../../../../../../../../../utils/reducers/getters'
import CustomerGDPRFile from '../../../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerOtherInformation/CustomerGdprFile'
import ModalDeleteCustomerGdprFile from '../../../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerOtherInformation/ModalDeleteCustomerGdprFile'
import useEditDeleteModal from '../../../../../../../../../utils/hooks/useEditDeleteModal'


interface ContactB2BOtherInformationProps extends ComponentWithPermissions {
    basicData: CustomerModelType | null
    getData: () => Promise<void>
}
interface ModifiedCustomerModelType extends CustomerModelType {
    customersForParse: string[]
}

const ContactB2BOtherInformation: React.FC<ContactB2BOtherInformationProps & ReduxCustomerGroups> = ({ basicData, userPermissions, customerGroups }) => {
    const { t } = useTwinTranslation()
    const { myCustomerGroups, getOtherData, otherData, taxRegimeCountry, setTaxRegimeCountry } = useContactB2BOtherInformationLogic(basicData)
    const { openModal, setOpenModal } = useEditDeleteModal()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const fields: FormRender<ModifiedCustomerModelType> = [
        {
            cols: 3,
            elements: [
                {
                    label: t('logo', 'Logotipo'),
                    name: 'image',
                    component: 'ImageInput',
                    defaultValue: otherData?.image
                },
                {
                    label: t('frontPhoto', 'Foto frontal'),
                    name: 'id_image_front',
                    component: 'ImageInput',
                    defaultValue: otherData?.id_image_front
                },
                {
                    label: t('backlPhoto', 'Foto trasera'),
                    name: 'id_image_back',
                    component: 'ImageInput',
                    defaultValue: otherData?.id_image_back
                },
            ]
        },
        {
            cols: 3,
            title: t('billingInformation', 'Información de facturación'),
            elements: [
                {
                    name: 'balance',
                    label: t('balance', 'Balance'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    defaultValue: otherData?.balance || ''
                },
                {
                    name: 'tax_regime_country',
                    label: t('taxRegimeCountry', 'Régimen Fiscal País'),
                    component: 'CustomSelectWithSearchBar',
                    items: getDictionaryCountries(),
                    value: taxRegimeCountry,
                    onChange: (value: string) => setTaxRegimeCountry(value)
                },
                {
                    name: 'tax_regime_state',
                    label: t('taxRegimeState', 'Régimen Fiscal Provincia'),
                    component: 'CustomSelectWithSearchBar',
                    sortItems: true,
                    items: getDictionaryStates(taxRegimeCountry),
                    value: otherData?.tax_regime_state,
                },
                {
                    name: 'useSecondTax',
                    component: 'CustomSelect',
                    label: t('useRechargeByDefault', 'Usar recargo por defecto'),
                    items: onOff
                },
                {
                    name: 'withoutVat',
                    label: t('exemptVat', 'Exento de IVA'),
                    component: 'CheckboxMargin',
                    defaultChecked: otherData?.withoutVat
                },
            ]
        },
        {
            cols: 1,
            title: t('customer', 'Tipos de Leads'),
            elements: [
                {
                    name: 'customersForParse',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: dictionaryComplexFromJsonArr(customerGroups?.B2BContactGroups || []),
                    defaultValue: myCustomerGroups
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: basicData?.id
                }
            ]
        },
        {
            cols: 2,
            title: t('gdpr', 'RGPD'),
            elements: [
                {
                    name: 'gdpr_accepted',
                    label: t('dataProtectionAct', 'Ley de protección de datos'),
                    component: 'CheckboxMargin',
                    defaultChecked: otherData?.gdpr_accepted
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: basicData?.id
                }
            ]
        }
    ]
    if (otherData && otherData?.gdpr_file) {
        fields[3].elements.push({
            name: 'gdpr_file',
            component: 'Custom',
            render: () => <CustomerGDPRFile deleteFile={() => setOpenModal({ type: 'delete', allRowData: { id: basicData?.id } })} data={otherData} />
        })
    } else {
        fields[3].elements.push({
            name: 'gdpr_file',
            label: t('file', 'Archivo'),
            component: 'FilesInput',
        })
    }
    return (
        <LoaderDecider loading={otherData ? false : true}>
            <SingleFormLayout action='/api/app/customer/updateCustomer' imagepath={['customer', String(basicData?.id)]} onSubmit={getOtherData} sections={fields} userPermissions={userPermissions} />
            {openModal?.type === 'delete' && <ModalDeleteCustomerGdprFile {...{ setOpened: () => setOpenModal(null) }} allRowData={openModal.allRowData} onSubmit={getOtherData} />}
        </LoaderDecider>
    )
}

export interface OtherCustomerInformation {
    balance: number
    tax_regime_country: string
    tax_regime_state: string
    CustomerGroups: number[]
    image: string
    id_image_front: string
    id_image_back: string
}
const useContactB2BOtherInformationLogic = (basicData: CustomerModelType | null) => {
    const [selectedOptions, setSelectedOptions] = useState<TwinDictionary>({})
    const [otherData, setOtherData] = useState<CustomerModelType | null>(null)
    const [taxRegimeCountry, setTaxRegimeCountry] = useState('')

    const getOtherData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/getOtherCustomerInformation', { "id": basicData?.id })
        if (result) {
            result.CustomerGroups = parseCustomerGroups(result.CustomerGroups)
        }
        setOtherData(result)
        if (result?.tax_regime_country) {
            setTaxRegimeCountry(result?.tax_regime_country)
        }
    }, [basicData])

    useEffect(() => {
        getOtherData()
    }, [getOtherData])

    return { otherData, getOtherData, taxRegimeCountry, setTaxRegimeCountry, selectedOptions, setSelectedOptions, myCustomerGroups: otherData?.CustomerGroups ?? [] }
}


const customerGroupsDispatch = {
    setCustomerGroups: (payload: CustomerGroupPayload) => ({ type: 'CHANGE_CUSTOMER_GROUP', payload }),
}

export type ReduxCustomerGroups = ConnectedProps<typeof customerGroupsConnect>
const mapCustomerGroupsConnector = (state: AllReduxPayloads) => ({ customerGroups: state.customerGroups })
const customerGroupsConnect = connect(mapCustomerGroupsConnector, customerGroupsDispatch)

const customerGroupsConnectLoading = withLoading(ContactB2BOtherInformation, [{ fetchUrl: '/api/app/customer/customerGroup/getAllCustomersGroup', propName: 'customerGroups', setFunctionName: 'setCustomerGroups' }])

export default customerGroupsConnect(customerGroupsConnectLoading);